import { useCallback, useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { log } from "../lib/logger";
import { Plus, X, Trash2, Loader2 } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [areaName, setAreaName] = useState("");
  // bump to trigger the data effect to refetch users + areas
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // Fetch users + areas whenever reloadTick changes.
  // setState happens after `await`, satisfying react-hooks/set-state-in-effect.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [a, b] = await Promise.all([api.get("/users"), api.get("/areas")]);
      if (cancelled) return;
      setUsers(a.data);
      setAreas(b.data);
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const onAddArea = async () => {
    if (!areaName.trim()) return;
    await api.post("/areas", { name: areaName.trim() });
    setAreaName("");
    reload();
  };

  const onDeleteUser = async (id) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  const onDeleteArea = async (id) => {
    if (!confirm("Delete this area?")) return;
    await api.delete(`/areas/${id}`);
    reload();
  };

  const canCreate = user?.role === "admin" || user?.role === "team_leader";

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Team &amp; Areas</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your team and operating areas.</p>
          </div>
          {canCreate && (
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="sohom-btn-primary" data-testid="new-user-btn">
              <Plus className="w-4 h-4" /> New User
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 sohom-card overflow-hidden" data-testid="users-table">
            <table className="sohom-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Phone</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} onClick={() => { setEditing(u); setShowForm(true); }} data-testid={`user-row-${u.email}`}>
                    <td className="font-medium text-slate-900">{u.full_name}</td>
                    <td>{u.email}</td>
                    <td><span className="sohom-chip status-NEW">{(u.role || "").replace("_", " ")}</span></td>
                    <td>{u.phone || "—"}</td>
                    <td>{u.is_active ? <span className="text-green-700 text-xs font-semibold">ACTIVE</span> : <span className="text-slate-400 text-xs font-semibold">INACTIVE</span>}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(user?.role === "admin" || (user?.role === "team_leader" && u.team_leader_id === user.id)) && u.id !== user.id && (
                        <button onClick={() => onDeleteUser(u.id)} className="p-1 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-user-${u.email}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sohom-card p-5" data-testid="areas-card">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Areas</h3>
            <div className="space-y-2 mb-3">
              {areas.length === 0 ? <div className="text-sm text-slate-400 italic">No areas yet.</div> : areas.map((a) => (
                <div key={a.id} className="flex items-center justify-between border border-slate-200 rounded px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{a.name}</div>
                    {(a.city || a.state) && <div className="text-xs text-slate-500">{a.city}{a.state ? ", " + a.state : ""}</div>}
                  </div>
                  {user?.role === "admin" && (
                    <button onClick={() => onDeleteArea(a.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
            {user?.role === "admin" && (
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <input className="sohom-input" placeholder="New area name" value={areaName} onChange={(e) => setAreaName(e.target.value)} data-testid="new-area-name" />
                <button onClick={onAddArea} className="sohom-btn-primary"><Plus className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <UserDialog
          initial={editing}
          areas={areas}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); reload(); }}
        />
      )}
    </Shell>
  );
}

function UserDialog({ initial, areas, onClose, onSaved }) {
  const { user: actor } = useAuth();
  const isEdit = !!initial;
  const isSelf = initial && actor.id === initial.id;
  const canSeeSensitive = actor.role === "admin" || isSelf;
  const [form, setForm] = useState({
    email: initial?.email || "",
    full_name: initial?.full_name || "",
    phone: initial?.phone || "",
    role: initial?.role || (actor.role === "team_leader" ? "field_executive" : "field_executive"),
    is_active: initial?.is_active ?? true,
    password: "",
    aadhaar_number: initial?.aadhaar_number || "",
    dl_number: initial?.dl_number || "",
    bank_account_number: initial?.bank_account_number || "",
    bank_ifsc: initial?.bank_ifsc || "",
    bank_beneficiary_name: initial?.bank_beneficiary_name || "",
    upi_id: initial?.upi_id || "",
    aadhaar_photo: initial?.aadhaar_photo || "",
    dl_photo: initial?.dl_photo || "",
    office_latitude: initial?.office_latitude ?? "",
    office_longitude: initial?.office_longitude ?? "",
    office_address: initial?.office_address || "",
    home_latitude: initial?.home_latitude ?? "",
    home_longitude: initial?.home_longitude ?? "",
    home_address: initial?.home_address || "",
    travel_origin: initial?.travel_origin || "office",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [geoBusy, setGeoBusy] = useState("");  // "" | "office" | "home"

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = (k) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set(k, reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      // Coerce numeric coords; drop empty strings so the field is not sent as ""
      const num = (v) => (v === "" || v === null ? null : Number(v));
      payload.office_latitude = num(payload.office_latitude);
      payload.office_longitude = num(payload.office_longitude);
      payload.home_latitude = num(payload.home_latitude);
      payload.home_longitude = num(payload.home_longitude);
      for (const k of ["office_latitude", "office_longitude", "home_latitude", "home_longitude"]) {
        if (Number.isNaN(payload[k])) payload[k] = null;
      }
      if (isEdit) {
        // If the viewer cannot see sensitive KYC/bank values (see _scrub_user), the form was
        // preloaded with masked strings ("****1234") or empty photos. Sending those back would
        // overwrite the real stored values. Strip them entirely — backend also enforces this.
        if (!canSeeSensitive) {
          for (const f of ["aadhaar_number","aadhaar_photo","dl_number","dl_photo","bank_account_number","bank_ifsc","bank_beneficiary_name","upi_id"]) {
            delete payload[f];
          }
        }
        await api.patch(`/users/${initial.id}`, payload);
      } else {
        if (!payload.password) { setErr("Password is required"); setSaving(false); return; }
        await api.post("/users", payload);
      }
      onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const captureLocation = (kind /* "office" | "home" */) => {
    if (!navigator.geolocation) { setErr("Geolocation not available in this browser."); return; }
    setGeoBusy(kind);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((f) => ({
          ...f,
          [`${kind}_latitude`]: latitude.toFixed(6),
          [`${kind}_longitude`]: longitude.toFixed(6),
        }));
        try {
          const r = await api.get(`/geocode/reverse?lat=${latitude}&lon=${longitude}`);
          if (r.data?.address) {
            setForm((f) => ({ ...f, [`${kind}_address`]: f[`${kind}_address`] || r.data.address }));
          }
        } catch (geoErr) { log.warn("[Sohom] reverse geocode failed (best-effort, ignoring):", geoErr); }
        setGeoBusy("");
      },
      (e) => { setGeoBusy(""); setErr("Location denied: " + e.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const showRole = actor.role === "admin" ? ["admin", "team_leader", "field_executive"] : ["field_executive"];

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="user-dialog">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>{isEdit ? `Edit ${initial.full_name}` : "New Team Member"}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full Name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="user-fullname-input" />
            <Field label="Email" value={form.email} onChange={(v) => set("email", v)} testid="user-email-input" type="email" />
            <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="user-phone" />
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Role</label>
              <select className="sohom-input mt-1" value={form.role} onChange={(e) => set("role", e.target.value)} disabled={isEdit && actor.role !== "admin"} data-testid="user-role">
                {showRole.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
              </select>
            </div>
            <Field label={isEdit ? "Password (leave blank to keep)" : "Password"} value={form.password} onChange={(v) => set("password", v)} testid="user-password" type="password" />
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} id="active" data-testid="user-active" />
              <label htmlFor="active" className="text-sm">Active</label>
            </div>
          </div>

          {form.role !== "admin" && (
            <>
              <div className="pt-3 border-t border-slate-100">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">KYC {canSeeSensitive ? "" : "(masked)"}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Aadhaar Number" value={form.aadhaar_number} onChange={(v) => set("aadhaar_number", v)} disabled={!canSeeSensitive} testid="kyc-aadhaar" />
                  <Field label="DL Number" value={form.dl_number} onChange={(v) => set("dl_number", v)} disabled={!canSeeSensitive} testid="kyc-dl" />
                  {canSeeSensitive && (
                    <>
                      <PhotoUpload label="Aadhaar Photo" value={form.aadhaar_photo} onChange={upload("aadhaar_photo")} onClear={() => set("aadhaar_photo", "")} testid="upload-aadhaar" />
                      <PhotoUpload label="DL Photo" value={form.dl_photo} onChange={upload("dl_photo")} onClear={() => set("dl_photo", "")} testid="upload-dl" />
                    </>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Bank {canSeeSensitive ? "" : "(masked)"}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Account Number" value={form.bank_account_number} onChange={(v) => set("bank_account_number", v)} disabled={!canSeeSensitive} testid="bank-acct" />
                  <Field label="IFSC" value={form.bank_ifsc} onChange={(v) => set("bank_ifsc", v)} disabled={!canSeeSensitive} testid="bank-ifsc" />
                  <Field label="Beneficiary Name" value={form.bank_beneficiary_name} onChange={(v) => set("bank_beneficiary_name", v)} disabled={!canSeeSensitive} testid="bank-beneficiary" />
                  <Field label="UPI ID" value={form.upi_id} onChange={(v) => set("upi_id", v)} disabled={!canSeeSensitive} testid="bank-upi" />
                </div>
              </div>
            </>
          )}

          {/* Travel — bookend point + home/office origins */}
          <div className="pt-3 border-t border-slate-100 space-y-4">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Travel reimbursement start point</h4>
              <div className="flex gap-2">
                {[
                  { v: "office", label: "From office", desc: "Daily round-trip is office → sites → office" },
                  { v: "home", label: "From home", desc: "Daily route is home → (office?) → sites → (office?) → home" },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className={`flex-1 cursor-pointer border rounded-lg p-3 transition-colors ${
                      form.travel_origin === opt.v ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`travel-origin-${opt.v}`}
                  >
                    <input
                      type="radio"
                      name="travel-origin"
                      value={opt.v}
                      checked={form.travel_origin === opt.v}
                      onChange={() => set("travel_origin", opt.v)}
                      className="mr-2"
                    />
                    <span className="font-semibold text-sm">{opt.label}</span>
                    <div className="text-[11px] text-slate-500 mt-1">{opt.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Office Origin <span className="text-slate-400 normal-case font-normal">(physical office)</span></h4>
                <button type="button" onClick={() => captureLocation("office")} disabled={geoBusy === "office"} className="sohom-btn-secondary text-xs" data-testid="office-use-location">
                  {geoBusy === "office" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} {geoBusy === "office" ? "Fetching…" : "Use current location"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Office Latitude" value={form.office_latitude ?? ""} onChange={(v) => set("office_latitude", v)} testid="office-lat" type="number" />
                <Field label="Office Longitude" value={form.office_longitude ?? ""} onChange={(v) => set("office_longitude", v)} testid="office-lng" type="number" />
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Office Address (optional)</label>
                  <input className="sohom-input mt-1" value={form.office_address} onChange={(e) => set("office_address", e.target.value)} placeholder="e.g. Sohom HQ, Kondapur, Hyderabad" data-testid="office-address" />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Home Origin <span className="text-slate-400 normal-case font-normal">(used when start point = home)</span></h4>
                <button type="button" onClick={() => captureLocation("home")} disabled={geoBusy === "home"} className="sohom-btn-secondary text-xs" data-testid="home-use-location">
                  {geoBusy === "home" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} {geoBusy === "home" ? "Fetching…" : "Use current location"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Home Latitude" value={form.home_latitude ?? ""} onChange={(v) => set("home_latitude", v)} testid="home-lat" type="number" />
                <Field label="Home Longitude" value={form.home_longitude ?? ""} onChange={(v) => set("home_longitude", v)} testid="home-lng" type="number" />
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Home Address (optional)</label>
                  <input className="sohom-input mt-1" value={form.home_address} onChange={(e) => set("home_address", e.target.value)} placeholder="e.g. Plot 24, Kondapur, Hyderabad" data-testid="home-address" />
                </div>
              </div>
            </div>

            <div className="text-[11px] text-slate-500">
              Office check-ins from the Dashboard&apos;s &ldquo;At office&rdquo; button become intermediate waypoints, so home→office→site→office→home is measured exactly as travelled.
            </div>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{err}</div>}
        </form>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="sohom-btn-secondary">Cancel</button>
          <button onClick={submit} className="sohom-btn-primary" disabled={saving} data-testid="save-user-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      <input type={type} className="sohom-input mt-1" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} />
    </div>
  );
}

function PhotoUpload({ label, value, onChange, onClear, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        {value ? (
          <div className="relative">
            <img src={value} alt="" className="w-16 h-16 object-cover rounded border border-slate-200" />
            <button type="button" onClick={onClear} className="absolute -top-1 -right-1 bg-white border border-slate-200 rounded-full p-0.5"><X className="w-3 h-3" /></button>
          </div>
        ) : null}
        <label className="sohom-btn-secondary cursor-pointer text-xs">
          {value ? "Replace" : "Upload"}
          <input type="file" accept="image/*" hidden onChange={onChange} data-testid={testid} />
        </label>
      </div>
    </div>
  );
}
